// frontend/src/pages/WorkOptimizationOperationsPage.tsx
import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  Operation, WorkType, OperationCategory,
  getOperations, createOperation, updateOperation, deleteOperation, getWorkTypes,
} from '../api/workOptimization';

const { Title } = Typography;

const CATEGORY_OPTIONS = [
  { value: 'productive', label: 'Полезная работа' },
  { value: 'walking', label: 'Ходьба / поиск инструмента' },
  { value: 'waiting', label: 'Ожидание' },
  { value: 'preparation', label: 'Подготовка' },
  { value: 'loss', label: 'Потеря (прочее)' },
];

const CATEGORY_COLORS: Record<string, string> = {
  productive: 'green', walking: 'orange',
  waiting: 'gold', preparation: 'blue', loss: 'red',
};

const WorkOptimizationOperationsPage: React.FC = () => {
  const [operations, setOperations] = useState<Operation[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Operation | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    load();
    getWorkTypes().then(setWorkTypes);
  }, []);

  const load = async () => {
    setLoading(true);
    try { setOperations(await getOperations()); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (op: Operation) => {
    setEditing(op);
    form.setFieldsValue({ name: op.name, category: op.category, work_type_id: op.work_type_id });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editing) {
        const updated = await updateOperation(editing.id, values);
        setOperations(prev => prev.map(o => o.id === updated.id ? updated : o));
        message.success('Обновлено');
      } else {
        const created = await createOperation(values);
        setOperations(prev => [...prev, created]);
        message.success('Создано');
      }
      setModalOpen(false);
    } catch { message.error('Ошибка при сохранении'); }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteOperation(id);
      setOperations(prev => prev.filter(o => o.id !== id));
      message.success('Удалено');
    } catch { message.error('Ошибка при удалении'); }
  };

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    {
      title: 'Категория', dataIndex: 'category', key: 'category',
      render: (v: string) => {
        const opt = CATEGORY_OPTIONS.find(o => o.value === v);
        return <Tag color={CATEGORY_COLORS[v]}>{opt?.label || v}</Tag>;
      },
    },
    { title: 'Тип работы', key: 'work_type', render: (_: any, r: Operation) => r.work_type?.name || <Tag>Универсальная</Tag> },
    {
      title: 'Действия', key: 'actions',
      render: (_: any, r: Operation) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Удалить операцию?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Справочник операций</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить операцию</Button>
      </div>
      <Table rowKey="id" dataSource={operations} columns={columns} loading={loading} pagination={{ pageSize: 50 }} />
      <Modal
        title={editing ? 'Редактировать операцию' : 'Новая операция'}
        open={modalOpen} onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()} okText="Сохранить"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label="Категория" rules={[{ required: true }]} initialValue="productive">
            <Select options={CATEGORY_OPTIONS} />
          </Form.Item>
          <Form.Item name="work_type_id" label="Тип работы (опционально)">
            <Select allowClear placeholder="Универсальная (подходит для всех)"
              options={workTypes.map(w => ({ value: w.id, label: w.name }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkOptimizationOperationsPage;
